import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Button, Input } from '@/components/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { metaInfoSchema } from '@/utils/validation';
import { showNotification } from '@/utils/showNotification';
import { useSetMetaForUi } from '@/api/queryHooks/useHostedCommerceUiController';
import styles from './Steps.module.css';

interface FormData {
  heading: string;
  description: string;
}
interface UploadLogoProps {
  setStep: (step: number) => void;
}

export const AddMeta = ({ setStep }: UploadLogoProps) => {
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(metaInfoSchema),
    defaultValues: { heading: '', description: '' },
    mode: 'onChange',
  });

  const { mutateAsync } = useSetMetaForUi();

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await mutateAsync(data);
      setStep(5);
    } catch (e) {
      showNotification();
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <h3 className={styles.title}>Please fill in meta information *</h3>
      <p className={styles.description}>We will need a heading and description to make your hosted page pop.</p>
      <Controller
        name="heading"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className={styles.input}
            label="Heading"
            placeholder="Manage crypto payments with ease"
            maxLength={51}
            errorMessage={fieldState?.error?.message}
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <Input
              {...field}
              multiline
              className={styles.textarea}
              label="Description"
              placeholder="300 characters max"
              maxLength={301}
              errorMessage={fieldState?.error?.message}
            />
          );
        }}
      />
      <div className={styles.buttons}>
        <Button fluid icon="chevronLeft" onPress={() => setStep(3)} variants="secondary" className={styles.button}>
          Move back
        </Button>
        <Button
          fluid
          icon="chevronRight"
          iconPosition="right"
          type="submit"
          className={styles.button}
          loading={isSubmitting}
          isDisabled={!isDirty || !isValid}
        >
          Next Step
        </Button>
      </div>
    </form>
  );
};
